'use client';

import { useRef } from 'react';
import Button from '@/components/sections/kit/Button';
import { REGISTER_URL } from '@/components/pricing/data';
import { gsap, useMaskedReveal, EASE_OUT } from '@/components/sections/kit/motion';
import type { CompareDossierData } from './types';
import { chipCounts } from './types';
import { CONTAINER, Chip } from './bits';
import { requestJump, useChoreo } from './motion';

/**
 * Dossier hero — eyebrow, two-line H1 (masked line reveal), lead, CTAs and
 * the verification chip row (counts derived from the table data so they can
 * never drift). "Skip to the table" hands off to the table's jump/flash.
 */
export default function DossierHero({ data }: { data: CompareDossierData }) {
  const rootRef = useRef<HTMLElement>(null);
  const counts = chipCounts(data);

  useMaskedReveal(rootRef, { heading: 'h1' });

  // The chips stamp in on their own clock, after the headline settles.
  useChoreo(rootRef, (root) => {
    const chips = root.querySelectorAll('[data-chip]');
    if (!chips.length) return;
    return gsap.from(chips, {
      y: 8,
      opacity: 0,
      scale: 0.92,
      duration: 0.5,
      ease: EASE_OUT,
      stagger: 0.09,
      delay: 0.55,
      paused: true,
    });
  });

  return (
    <section
      ref={rootRef}
      className="pt-[calc(var(--grid-cell,72px)+56px)] md:pt-[calc(var(--grid-cell,72px)+96px)]"
    >
      <div className={CONTAINER}>
        <span data-head-sub className="block font-jetbrains text-[11px] font-semibold tracking-[0.16em]">
          <span className="text-brand-600">COMPARE</span>
          <span className="text-dim"> — RII.LINK VS {data.competitor.toUpperCase()}</span>
        </span>
        <h1 className="mt-[18px] max-w-[880px] font-onest text-[clamp(38px,5.4vw,58px)] font-extrabold leading-[1.06] tracking-[-0.03em]">
          {data.hero.titleLine1}
          <br />
          <span className="text-muted">{data.hero.titleLine2}</span>
        </h1>
        <p data-head-sub className="mt-6 max-w-[640px] text-[16.5px] leading-[1.65] text-pretty text-muted">
          {data.hero.lead}
        </p>
        <div data-head-sub className="mt-7 flex flex-wrap items-center gap-4">
          <Button variant="primary" href={REGISTER_URL}>
            Start free — no card
          </Button>
          <button
            type="button"
            onClick={() => requestJump(0)}
            className="cursor-pointer rounded-sm text-sm font-semibold text-brand-600 hover:text-brand-700 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-600"
          >
            Skip to the table &darr;
          </button>
        </div>
        <div className="mt-11 flex flex-wrap gap-2.5">
          <span data-chip>
            <Chip dot>VERIFIED {data.verified}</Chip>
          </span>
          <span data-chip>
            <Chip>
              {counts.claims} CLAIMS &middot; {counts.sources} SOURCES
            </Chip>
          </span>
          <span data-chip>
            <Chip>{counts.concessions} CONCESSIONS — IN FULL</Chip>
          </span>
        </div>
      </div>
    </section>
  );
}
